import { Autocomplete, TextField } from '@mui/material';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { Breadcrumbs } from '@weareopenr/component-library';
import { IVenueCategory } from 'src/api/getVenueCategories';
import { getHierarchyNames } from 'src/helpers';
import { DEBOUNCE_DURATION } from 'src/constants';
import { filterValidMoveToCategories } from '../utilities/filterValidMoveToCategories';
import styles from './MoveToCategory.module.scss';

const MAX_BREADCRUMB_LEVEL = 2;

export const MoveToCategory = ({
  orderedCategories,
  category,
  onSubmit: onSubmitCallback,
}: {
  orderedCategories: IVenueCategory[];
  category: IVenueCategory;
  onSubmit: (newParentId: string | null) => void;
}) => {
  const { handleSubmit, control } = useForm<FieldValues>({
    defaultValues: { moveToCategory: null },
  });
  const [delayedSearchQuery, setDelayedSearchQuery] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const categories = useMemo(() => {
    const validCategories = filterValidMoveToCategories(category, orderedCategories);

    return validCategories.map((category) => ({
      ...category,
      breadcrumbs: getHierarchyNames(category, orderedCategories, {
        id: 'category_id',
        parent_id: 'parent_category_id',
      }).map((breadcrumb) => ({ label: breadcrumb })),
    }));
  }, [orderedCategories, category]);

  const filteredCategories = useMemo(() => {
    if (!delayedSearchQuery) return categories;

    const searchValue = delayedSearchQuery.toLowerCase();
    return categories.filter((cat) => cat.name.toLowerCase().includes(searchValue));
  }, [categories, delayedSearchQuery]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDelayedSearchQuery(searchQuery);
    }, DEBOUNCE_DURATION);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const onSubmit = (data: FieldValues) => onSubmitCallback(data.moveToCategory?.category_id ?? null);

  return (
    <form id="move-to-category-form" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="moveToCategory"
        control={control}
        rules={{ required: 'Please select a category' }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Autocomplete
            className={styles['autocomplete']}
            options={filteredCategories}
            getOptionLabel={(option: IVenueCategory) => option.name || ''}
            isOptionEqualToValue={(option: IVenueCategory, value: IVenueCategory) =>
              option?.category_id === value?.category_id
            }
            onClose={() => {
              setSearchQuery('');
              setDelayedSearchQuery('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose a Parent Category"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                error={Boolean(error)}
                helperText={error?.message}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} className={styles['list-option']}>
                <Breadcrumbs breadcrumbs={option.breadcrumbs} maxLevel={MAX_BREADCRUMB_LEVEL} />
              </li>
            )}
            value={value}
            onChange={(_e: SyntheticEvent<Element, Event>, category: IVenueCategory) => onChange(category)}
          />
        )}
      />
    </form>
  );
};
